import React from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Avatar,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search,
  Dashboard,
  PointOfSale,
  Inventory,
  ReceiptLong,
  PeopleAlt,
  ArrowForward,
  LocalShipping,
  Receipt,
  AttachMoney,
  Assessment,
  Restaurant,
  Paid,
  PieChart,
  SupportAgent,
  Email,
  Phone,
  LiveHelp
} from '@mui/icons-material';
import { keyframes } from '@emotion/react';

const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
`;

const SupportPage = () => {
  const theme = useTheme();

  const helpCategories = [
    {
      title: 'Tableau de Bord',
      icon: <Dashboard sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
      links: ['Vue globale', 'Indicateurs clés', 'Alertes urgentes', 'Personnalisation']
    },
    {
      title: 'Gestion de Caisse',
      icon: <PointOfSale sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
      links: ['Transactions en direct', 'Rapports journaliers', 'Intégration comptable', 'Problèmes de paiement']
    },
    {
      title: 'Gestion des Fournisseurs',
      icon: <LocalShipping sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
      links: ['Commandes fournisseurs', 'Suivi livraisons', 'Factures fournisseurs', 'Évaluation fournisseurs']
    },
    {
      title: 'Gestion des RH',
      icon: <PeopleAlt sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
      links: ['Gestion des contrats', 'Accomptes salariés', 'Planning des congés', 'Bulletins de paie']
    },
    {
      title: 'Gestion de Stock',
      icon: <Inventory sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
      links: ['Niveaux de stock', 'Calcul food cost', 'Alertes péremption', 'Inventaire physique']
    },
    {
      title: 'Analytiques Avancées',
      icon: <PieChart sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
      links: ['Analyse des ventes', 'Rentabilité par plat', 'Tendances saisonnières', 'Benchmarking']
    }
  ];

  return (
    <Box sx={{
      bgcolor: 'background.default',
      minHeight: '100vh',
      backgroundImage: `linear-gradient(to bottom right, ${alpha(theme.palette.primary.dark, 0.1)}, ${alpha(theme.palette.secondary.dark, 0.05)})`
    }}>
      {/* En-tête */}
      <Box sx={{
        pt: 8,
        pb: 12,
        backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `url('/restaurant-kitchen-blur.jpg')`,
          backgroundSize: 'cover',
          opacity: 0.1,
          zIndex: 0
        }
      }}>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar sx={{
              bgcolor: 'white',
              width: 80,
              height: 80,
              mb: 3,
              animation: `${pulse} 2s infinite`,
              '& svg': {
                color: theme.palette.primary.main,
                fontSize: 40
              }
            }}>
              <Restaurant />
            </Avatar>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 800 }}>
              Support RestaurantPro
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Assistance complète pour votre gestion de restaurant
            </Typography>
          </Box>

          <TextField
            fullWidth
            variant="outlined"
            placeholder="Comment pouvons-nous vous aider ?"
            InputProps={{
              startAdornment: <Search sx={{ color: 'white', mr: 1 }} />,
              sx: {
                bgcolor: alpha(theme.palette.primary.light, 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.light, 0.3)
                }
              }
            }}
          />
        </Container>
      </Box>

      {/* Accès Rapide */}
      <Container maxWidth="lg" sx={{ mt: -6, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {[
            { text: 'Générer Facture', icon: <Receipt />, color: 'success' },
            { text: 'Suivi Stock', icon: <Inventory />, color: 'warning' },
            { text: 'Analytiques Food Cost', icon: <AttachMoney />, color: 'info' },
            { text: 'Fournisseurs', icon: <LocalShipping />, color: 'error' }
          ].map((action, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper sx={{
                p: 3,
                borderRadius: 3,
                textAlign: 'center',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[6]
                }
              }}>
                <Avatar sx={{
                  bgcolor: `${action.color}.main`,
                  width: 56,
                  height: 56,
                  mb: 2,
                  mx: 'auto'
                }}>
                  {action.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {action.text}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contenu Principal */}
      <Container maxWidth="xl">
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {/* Catégories d'Aide */}
          {helpCategories.map((category, index) => (
              <Grid
              item
              xs={12}
              md={6}
              lg={4}
              key={index}
              sx={{ mb: 4 }} // Only vertical spacing between rows
            >
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  height: '100%',
                  borderLeft: `4px solid ${category.color}`,
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[6]
                  }
                }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Box sx={{ color: category.color, mr: 2 }}>{category.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {category.title}
                  </Typography>
                </Box>
                <List disablePadding>
                  {category.links.map((link, linkIndex) => (
                    <ListItem
                      button
                      key={linkIndex}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          bgcolor: alpha(category.color, 0.1),
                          '& .MuiListItemIcon-root': {
                            color: category.color
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <SupportAgent sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={link}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        

        {/* Support Channels */}
<Grid item xs={12} lg={6}>
  <Paper sx={{ 
    p: 4,
    borderRadius: 4,
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
    color: 'white',
    boxShadow: theme.shadows[8],
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)'
    }
  }}>
    <Typography variant="h4" gutterBottom sx={{ 
      fontWeight: 200,
      mb: 4,
      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
    }}>
      Enterprise Support Channels
    </Typography>
    <Grid container spacing={4}>
      {[
        { 
          title: 'Social Media', 
          icon: <LiveHelp sx={{ fontSize: 22 }} />,
          action: 'Start Chat'
        },
        { 
          title: 'Email Support', 
          icon: <Email sx={{ fontSize: 22 }} />,
          action: 'Send Email'
        },
        { 
          title: 'Phone Support', 
          icon: <Phone sx={{ fontSize: 22 }} />,
          action: 'Call Now'
        },
        { 
          title: 'Ticket System', 
          icon: <ReceiptLong sx={{ fontSize: 22 }} />,
          action: 'Create Ticket'
        }
      ].map((channel, index) => (
        <Grid item xs={12} sm={6} key={index}>
          <Box sx={{ 
            p: 3,
            borderRadius: 3,
            bgcolor: alpha(theme.palette.common.white, 0.08),
            height: '80%',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.12),
              transform: 'translateY(-2px)'
            }
          }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Box sx={{
                p: 1.5,
                mr: 2,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.common.white, 0.2),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {channel.icon}
              </Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 700,
                letterSpacing: 0.5
              }}>
                {channel.title}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              sx={{ 
                textTransform: 'none',
                fontWeight: 700,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1rem',
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[6],
                  bgcolor: theme.palette.secondary.dark
                }
              }}
              startIcon={<ArrowForward />}
            >
              {channel.action}
            </Button>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Paper>
</Grid>


          {/* Centre d'Aide */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 800 }}>
                Centre de Ressources
              </Typography>
              <Grid container spacing={2}>
                {[
                  'Guide de gestion des stocks',
                  'Optimisation du food cost',
                  'Modèles de facturation',
                  'Manuel RH complet',
                  'Tutoriels analytiques',
                  'FAQ fournisseurs'
                ].map((resource, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Chip
                      label={resource}
                      variant="outlined"
                      sx={{
                        width: '100%',
                        justifyContent: 'flex-start',
                        py: 2,
                        borderRadius: 1,
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        py: 6,
        borderTop: `1px solid ${theme.palette.divider}`
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center" mb={2}>
                <Restaurant sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  CulinaryERP
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Empowering restaurants with intelligent management solutions since 2023
              </Typography>
            </Grid>

            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Solutions
              </Typography>
              <List dense>
                {['POS Systems', 'Inventory Mgmt', 'Staff Scheduling', 'Analytics'].map((item) => (
                  <ListItem key={item} disableGutters>
                    <Link href="#" variant="body2" color="textSecondary">
                      {item}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={6} md={2}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Resources
              </Typography>
              <List dense>
                {['Blog', 'Webinars', 'Case Studies', 'API Docs'].map((item) => (
                  <ListItem key={item} disableGutters>
                    <Link href="#" variant="body2" color="textSecondary">
                      {item}
                    </Link>
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Connect With Us
              </Typography>
              <Box display="flex" gap={2}>
                {['Facebook', 'LinkedIn', 'Twitter', 'Instagram'].map((network) => (
                  <Button
                    key={network}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 2
                    }}
                  >
                    {network}
                  </Button>
                ))}
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="textSecondary">
              © {new Date().getFullYear()} CulinaryERP. All rights reserved.
            </Typography>
            <Box display="flex" gap={3}>
              <Link href="#" variant="body2" color="textSecondary">
                Privacy Policy
              </Link>
              <Link href="#" variant="body2" color="textSecondary">
                Terms of Service
              </Link>
              <Link href="#" variant="body2" color="textSecondary">
                Cookie Settings
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SupportPage;